import React, { useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import Action from "components/ActionPopOver";
import SearchComp from "components/Searchcomponent";
import { MultiSelect } from "components/MultiSelect/MultiSelect";
import AuthService from "Lib/API/Auth";
import moment from "moment";
import { toastr } from "react-redux-toastr";
import CreateArea from "./CreateNot";
import EventNoteRoundedIcon from "@mui/icons-material/EventNoteRounded";
import "./index.scss";

const NoteTags = ({ tags }) => {
  const list = Array.isArray(tags)
    ? tags.filter((t) => typeof t === "string" && t)
    : [];
  if (!list.length) return null;
  return (
    <div className="note-tags">
      {list.map((tag) => (
        <span className="note-tag" key={tag}>
          {tag}
        </span>
      ))}
    </div>
  );
};

const Notes = ({ details, notes, handleAdd, handleDelete, handleEditNote, refresh, setRefresh, loading, setLoading }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [search, setSearch] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [noteTagOptions, setNoteTagOptions] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [editNote, setEditNote] = useState(false);
  const [noteToEdit, setNoteToEdit] = useState("");

  const list = Array.isArray(notes) ? notes : [];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setTagsLoading(true);
      try {
        const token = await getAccessTokenSilently();
        const res = await AuthService.getTags("notes", "", "", token);
        if (cancelled) return;
        const tags = (res?.result || [])
          .map((item) => ({
            title: item?.title,
            id: item?._id || item?.title,
          }))
          .filter((t) => typeof t.title === "string" && t.title)
          .sort((a, b) => a.title.localeCompare(b.title));
        setNoteTagOptions(tags);
      } catch (err) {
        if (!cancelled) {
          toastr.error("Error", err?.data?.message || "Something went wrong");
        }
      } finally {
        if (!cancelled) setTagsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently, refresh]);

  const tagOptions = useMemo(() => {
    const defined = new Set(noteTagOptions.map((o) => o.title));
    const extra = [
      ...new Set(list.flatMap((n) => (Array.isArray(n.tags) ? n.tags : []))),
    ]
      .filter((t) => typeof t === "string" && t && !defined.has(t))
      .sort((a, b) => a.localeCompare(b))
      .map((t) => ({ title: t, id: t }));
    return [{ title: "Untagged", id: "untagged" }, ...noteTagOptions, ...extra];
  }, [list, noteTagOptions]);

  const tagFormik = useMemo(
    () => ({
      values: { account: { noteTags: selectedTags } },
      setFieldValue: (name, value) => {
        if (name === "account") setSelectedTags(value?.noteTags || []);
      },
    }),
    [selectedTags]
  );

  const handleTagChange = (newValue) => {
    setSelectedTags(newValue?.[0] === "-1" ? ["-1"] : newValue || []);
  };

  const filteredNotes = useMemo(() => {
    const q = search.trim().toLowerCase();
    const selected =
      selectedTags[0] === "-1"
        ? []
        : selectedTags.map((t) =>
            t.id === "untagged" ? "untagged" : t.title
          );
    return list.filter((n) => {
      const noteText = typeof n.note === "string" ? n.note.toLowerCase() : "";
      const noteTags = Array.isArray(n.tags) ? n.tags : [];
      const matchesSearch = !q || noteText.includes(q);
      const matchesTag =
        !selected.length ||
        selected.some((key) =>
          key === "untagged" ? noteTags.length === 0 : noteTags.includes(key)
        );
      return matchesSearch && matchesTag;
    });
  }, [list, search, selectedTags]);

  const filterBar = (
    <>
      <div className={`note-list-filters${details ? "" : " note-list-filters--stack"}`}>
        <SearchComp
          value={search}
          setSearchText={(e) => setSearch(e.target.value)}
          placeholder="Search notes"
          style={{ backgroundColor: "#fff" }}
          id="notes-search"
          name="notes-search"
          autoComplete="off"
          height={48}
        />
        <div className="note-tag-select">
          <MultiSelect
            placeholder="Select..."
            isCheckBoxOption={true}
            options={tagOptions}
            loading={tagsLoading}
            formik={tagFormik}
            formikField="account[noteTags]"
            formikKey="noteTags"
            isAccount={true}
            handleFormikChange={handleTagChange}
            searchAction={() => {}}
            total={tagOptions.length}
            width="100%"
            popperClassName="note-tag-select-menu"
          />
        </div>
      </div>
      {filteredNotes.length === 0 && list.length > 0 && (
        <div className="note-empty">No notes match this search or tag.</div>
      )}
    </>
  );

  if (!details) {
    return (
      <>
        <CreateArea
          handleAdd={handleAdd}
          editNote={editNote}
          setEditNote={setEditNote}
          noteToEdit={noteToEdit}
          setNoteToEdit={setNoteToEdit}
          refresh={refresh}
          setRefresh={setRefresh}
          loading={loading}
          setLoading={setLoading}
        />
        {filterBar}
        {filteredNotes?.map((note, index) => (
          <div className="member-card py-3" key={index}>
            <div className="card-content">
              <div className="member-container w-100">
                <div className="member-name d-flex justify-content-between w-100">
                  {note?.organization?.orgName}
                  <div className="member-t mr-3">
                    <span className="mr-2">By {`${note?.added_by?.first_name} ${note?.added_by?.last_name}`} on</span>
                    {moment(note?.createdAt).utcOffset("+0100").format("L")}
                  </div>
                </div>
              </div>
              <div className="cursor-pointer mr-2">
                <Action
                  onDeleteClick={() => handleDelete(note._id)}
                  onEditNoteClick={() => {
                    setEditNote(true);
                    setNoteToEdit(note);
                  }}
                  style={{
                    color: "#4D5565",
                    width: "14px",
                    height: "14px",
                  }}
                  checkActive={true}
                />
              </div>
            </div>
            <div className="note-body">{note.note}</div>
            <NoteTags tags={note.tags} />
          </div>
        ))}
      </>
    );
  }

  return (
    <>
      {filterBar}
      {filteredNotes?.map((note, index) => (
        <div className="d-flex my-10" key={index}>
          <div className="col-sm-1 px-0">
            <EventNoteRoundedIcon
              className="note-icon p-2"
              fontSize="large"
            />
            <div className="note-line">
              <br />
            </div>
          </div>
          <div className="member-card col-sm-11 pl-0 mt-0">
            <div className="card-content">
              <div className="member-container w-100">
                <div
                  className="member-name d-flex justify-content-between w-100"
                  style={{
                    color: "#2455EF",
                    fontSize: "18px",
                    fontWeight: 500,
                  }}
                >
                  {note?.organization?.orgName}
                  <div
                    className="member-t mr-3"
                    style={{
                      fontSize: "15px",
                    }}
                  >
                    <span className="mr-2">By {`${note?.added_by?.first_name} ${note?.added_by?.last_name}`} on</span>
                    {moment(note?.createdAt).utcOffset("+0100").format("L")}
                  </div>
                </div>
              </div>
              <div className="cursor-pointer mr-2">
                <Action
                  onDeleteClick={() => handleDelete(note._id)}
                  onEditNoteClick={() => handleEditNote(note)}
                  style={{
                    color: "#4D5565",
                    width: "14px",
                    height: "14px",
                  }}
                  checkActive={true}
                />
              </div>
            </div>
            <div
              className="note-body"
              style={{
                color: "#666667",
                fontSize: "18px",
                fontWeight: 500,
                wordBreak: "break-word",
                overflowWrap: "break-word",
              }}
            >
              {note.note}
            </div>
            <NoteTags tags={note.tags} />
          </div>
        </div>
      ))}
    </>
  );
};

export default Notes;
